import { type ReactNode, useEffect, useState } from "react";

import { isElectron } from "~/env";
import { useResizableWidth } from "~/hooks/useResizableWidth";
import { cn } from "~/lib/utils";

import { RightPanelResizeHandle } from "./RightPanelResizeHandle";

export type PreviewPanelMode = "inline" | "sheet" | "sidebar" | "embedded";

const PREVIEW_PANEL_WIDTH_STORAGE_KEY = "t3code:preview-panel-width";
const PREVIEW_PANEL_MIN_WIDTH = 360;
/** Fraction of the viewport allowed, preserving the remaining space for chat. */
const PREVIEW_PANEL_MAX_WIDTH_FRACTION = 0.7;
const PREVIEW_PANEL_DEFAULT_WIDTH = 540;
export const INLINE_PANEL_TRANSITION_MS = 380;

export function getPreviewPanelMaxWidth(viewportWidth: number): number {
  return Math.floor(viewportWidth * PREVIEW_PANEL_MAX_WIDTH_FRACTION);
}

/**
 * Shell for the preview panel. In inline mode the panel is user-resizable
 * via a drag handle on the left edge; width persists per browser. In
 * sheet/sidebar modes the parent owns the size.
 */
export function PreviewPanelShell(props: {
  mode: PreviewPanelMode;
  maximized?: boolean;
  open?: boolean;
  children: ReactNode;
}) {
  const useDragRegion = isElectron && props.mode !== "sheet" && props.mode !== "embedded";
  const isInline = props.mode === "inline";
  const open = props.open ?? true;
  const maxWidth = useViewportClampedMaxWidth();
  const { width, isResizing, handlers } = useResizableWidth({
    storageKey: PREVIEW_PANEL_WIDTH_STORAGE_KEY,
    defaultWidth: PREVIEW_PANEL_DEFAULT_WIDTH,
    minWidth: PREVIEW_PANEL_MIN_WIDTH,
    maxWidth,
    edge: "left",
  });
  const retainInlineContent = useRetainedInlineContent(open);

  if (!isInline) {
    return (
      <div
        className="relative flex h-full min-h-0 min-w-0 w-full flex-col self-stretch bg-background"
        data-preview-panel-mode={props.mode}
        data-preview-panel-maximized={props.maximized ? "true" : "false"}
      >
        {useDragRegion ? <div className="electron-drag-region h-0 w-full" aria-hidden /> : null}
        {props.children}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative h-full min-h-0 min-w-0 self-stretch overflow-hidden",
        isResizing
          ? "transition-none"
          : "transition-[width,opacity] duration-[380ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none",
        props.maximized ? "flex-1" : "shrink-0",
        open ? "opacity-100" : "pointer-events-none opacity-0",
      )}
      style={!props.maximized ? { width: open ? `${width}px` : "0px" } : undefined}
      data-preview-panel-mode={props.mode}
      data-preview-panel-maximized={props.maximized ? "true" : "false"}
      data-preview-panel-open={open ? "true" : "false"}
      aria-hidden={open ? undefined : true}
      inert={open ? undefined : true}
    >
      {retainInlineContent ? (
        <div
          className="absolute inset-y-0 right-0 flex min-h-0 min-w-0 flex-col border-l border-border bg-background"
          style={{ width: props.maximized ? "100%" : `${width}px` }}
        >
          {!props.maximized ? (
            <RightPanelResizeHandle handlers={handlers} className="left-0" />
          ) : null}
          {useDragRegion ? <div className="electron-drag-region h-0 w-full" aria-hidden /> : null}
          {props.children}
        </div>
      ) : null}
    </div>
  );
}

function useRetainedInlineContent(open: boolean): boolean {
  const [retained, setRetained] = useState(open);

  useEffect(() => {
    if (open) {
      setRetained(true);
      return;
    }
    if (!retained) return;

    const timeout = setTimeout(() => setRetained(false), INLINE_PANEL_TRANSITION_MS);
    return () => clearTimeout(timeout);
  }, [open, retained]);

  return open || retained;
}

/**
 * Track viewport width to derive a sensible upper bound for the panel.
 * Resize-aware so dragging the OS window narrower re-clamps the stored
 * width on the next render (the hook's clamp picks this up automatically).
 */
function useViewportClampedMaxWidth(): number {
  const [vw, setVw] = useState(() => (typeof window === "undefined" ? 1280 : window.innerWidth));
  useEffect(() => {
    if (typeof window === "undefined") return;
    let frame = 0;
    const onResize = () => {
      // Coalesce rapid resize events into one rAF tick.
      if (frame !== 0) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        setVw(window.innerWidth);
      });
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, []);
  return getPreviewPanelMaxWidth(vw);
}
